import { useState, useEffect, ComponentProps } from 'react';
import { Moment } from 'moment';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DateCalendar, useTheme, PickersDay } from '../../../base';
import {
    StyledDateRangePanel,
    StyledPanelTitle,
} from './CustomDateRangePicker.styles';

interface ICustomDateRangePickerProps {
    fromDate: Moment | null;
    toDate: Moment | null;
    onRangeSelect: (fromDate: Moment, toDate: Moment) => void;
}

const CustomDateRangePicker = ({
    fromDate,
    toDate,
    onRangeSelect,
}: ICustomDateRangePickerProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [rangeStart, setRangeStart] = useState<Moment | null>(fromDate);
    const [rangeEnd, setRangeEnd] = useState<Moment | null>(toDate);

    useEffect(() => {
        setRangeStart(fromDate);
        setRangeEnd(toDate);
    }, [fromDate, toDate]);

    const handleDateClick = (date: Moment | null) => {
        if (!date) return;

        if (!rangeStart || rangeEnd) {
            setRangeStart(date);
            setRangeEnd(null);
        } else {
            let start = rangeStart;
            let end = date;
            if (date.isBefore(rangeStart)) {
                start = date;
                end = rangeStart;
            }
            setRangeStart(start);
            setRangeEnd(end);
            onRangeSelect(start, end);
        }
    };

    const renderDay = (props: ComponentProps<typeof PickersDay>) => {
        const { day, outsideCurrentMonth, ...other } = props;

        const isStart = rangeStart && day.isSame(rangeStart, 'day');
        const isEnd = rangeEnd && day.isSame(rangeEnd, 'day');
        const isInRange =
            rangeStart &&
            rangeEnd &&
            day.isAfter(rangeStart, 'day') &&
            day.isBefore(rangeEnd, 'day');

        return (
            <PickersDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                sx={{
                    ...(isStart || isEnd
                        ? {
                              backgroundColor: `${theme.palette.primary.light} !important`,
                              color: `${theme.palette.primary.main} !important`,
                              borderRadius: '50%',
                          }
                        : {}),
                    ...(isInRange
                        ? {
                              color: `${theme.palette.primary.main} !important`,
                          }
                        : {}),
                    ...(outsideCurrentMonth
                        ? {
                              color: `${theme.palette.grey[300]} !important`,
                          }
                        : {}),
                }}
            />
        );
    };

    return (
        <StyledDateRangePanel>
            <StyledPanelTitle variant="subtitle1">
                {t('reportsPage.filter.datePickers')}
            </StyledPanelTitle>
            <DateCalendar
                value={rangeStart}
                onChange={handleDateClick}
                maxDate={moment()}
                slots={{
                    day: renderDay,
                }}
                showDaysOutsideCurrentMonth
            />
        </StyledDateRangePanel>
    );
};

export default CustomDateRangePicker;
